import {
  ILocation,
  IOrder,
  IOrderMessage,
  IVehicle,
  OrderDirection,
  OrderEventStatus,
} from '@boma/supplier-portal/shared/entities';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import * as SalesActions from './sales.actions';

export const SALES_FEATURE_KEY = 'sales';

export interface SalesState extends EntityState<IOrder> {
  selectedId?: string | number; // which Orders record has been selected
  loaded: boolean; // has the Orders list been loaded
  error?: string | null; // last known error (if any)
  addingOrUpdatingEvent: boolean;
  locationIds?: string[];
  customerIds?: string[];
  dateRange?: { fromDate: string; toDate: string };
  nextPage?: number;
  perPage: string;
  query?: string;
  locationType?: string;
  locations: ILocation[] | IVehicle[];
  orderDirection: OrderDirection;
  messages: IOrderMessage[] | null;
  status: OrderEventStatus | null;
  statuses: string[];
  orderType: string;
}

export interface SalesPartialState {
  readonly [SALES_FEATURE_KEY]: SalesState;
}

export const salesAdapter: EntityAdapter<IOrder> = createEntityAdapter<
  IOrder
>();

export const salesInitialState: SalesState = salesAdapter.getInitialState({
  // set initial required properties

  loaded: false,
  locationType: null,
  nextPage: 1,
  perPage: '25',
  locations: [],
  locationIds: [],
  customerIds: [],
  dateRange: null,
  addingOrUpdatingEvent: false,
  orderDirection: OrderDirection.Outbound,
  messages: null,
  status: null,
  statuses: ['Fulfilled', 'Cancelled', 'Returned'],
  orderType: null,
});

export const salesReducer = createReducer(
  salesInitialState,
  on(SalesActions.loadSales, (state) => ({
    ...state,
    loaded: false,
    error: null,
  })),
  on(
    SalesActions.updateSaleSearchParams,
    (
      state,
      {
        query,
        locationIds,
        locationType,
        orderDirection,
        status,
        statuses,
        customerIds,
        dateRange,
        orderType,
      }
    ) =>
      salesAdapter.removeAll({
        ...state,
        query,
        locationIds,
        locationType,
        nextPage: 1,
        loaded: false,
        orderDirection,
        status,
        statuses,
        customerIds,
        dateRange,
        orderType,
      })
  ),
  on(SalesActions.loadSalesSuccess, (state, { payload }) =>
    salesAdapter.upsertMany(payload.items, {
      ...state,
      loaded: true,
      nextPage: payload.nextPage,
    })
  ),
  on(SalesActions.loadSaleDetails, (state) => ({
    ...state,
    loaded: false,
    messages: null,
    error: null,
  })),
  on(SalesActions.loadSaleDetailsSuccess, (state, { order }) =>
    salesAdapter.upsertOne(order, {
      ...state,
      selectedId: order.id,
      loaded: true,
    })
  ),
  on(SalesActions.loadSaleDetailsFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(SalesActions.loadSalesFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(SalesActions.loadSaleLocationsSuccess, (state, { payload }) => ({
    ...state,
    locations: payload,
  })),
  on(SalesActions.loadSaleMessagesSuccess, (state, { payload }) => {
    let newMessageArray = [...(state.messages ?? [])].concat(payload);
    newMessageArray = [
      ...new Map(newMessageArray.map((item) => [item.id, item])).values(),
    ];
    newMessageArray.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return {
      ...state,
      messages: [...newMessageArray],
    };
  }),
  on(SalesActions.createSaleMessageSuccess, (state, { message }) => {
    let newMessageArray = [...(state.messages ?? [])].concat([message]);
    newMessageArray = [
      ...new Map(newMessageArray.map((item) => [item.id, item])).values(),
    ];
    newMessageArray.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    return {
      ...state,
      messages: [...newMessageArray],
    };
  })
);
